import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  CheckCircle2,
  Coins,
  Info,
  Loader2,
  ShieldAlert,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  clearingIsSufficient,
  newIdempotencyKey,
  treasuryService,
  type StrandedHold,
  type TreasuryResult,
} from '@services/treasury.service';
import { cn, formatCurrency } from '@lib/utils';
import { shortId } from '@pages/finance/ledgerFormat';

/** Both API clients funnel errors through `extractMessage`, so the backend's
 *  human message (HOLD_EXCEEDS_REFERENCE, BASKET_STILL_LIVE …) is on `.message`. */
function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

function when(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

type Step = 'pick' | 'review' | 'preview' | 'done';

export interface ClearStrandedHoldModalProps {
  open: boolean;
  onClose: () => void;
  /** Scope the worklist to one wallet — the wallet whose card was clicked. */
  walletId: string;
  /** Nullable because system wallets (chekit_escrow, chekit_commission…) have no
   *  owner to name — `LedgerWalletDetail.ownerLabel` is `string | null`. */
  walletLabel?: string | null;
  onDone?: () => void;
}

/**
 * CLEAR A STRANDED HOLD — the guided version of `adjust-hold`.
 *
 * The generic treasury modal asks the operator to type a wallet, an amount and
 * a reason into empty boxes. For a stranded hold that is three chances to get
 * it wrong, and the wallet-level held_balance is NOT a safe ceiling: a wallet
 * holding 1.70 across four baskets — two finished, two in flight — will accept
 * a 1.70 clear and silently wipe the two live earmarks.
 *
 * So this screen never asks for a number. It works one basket at a time,
 * showing what the reads service already worked out:
 *   • how the money entered (payment → escrow),
 *   • what has happened to it since (refunded? released? still in escrow?),
 *   • the verdict — where the money is SUPPOSED to go,
 *   • the exact outstanding earmark, prefilled and capped.
 *
 * The amount is bounded by that ONE basket's outstanding hold, and the backend
 * re-derives the same ceiling (`assertClearable`) rather than trusting this
 * form — the guard has to hold for API callers too.
 */
export function ClearStrandedHoldModal({
  open,
  onClose,
  walletId,
  walletLabel,
  onDone,
}: ClearStrandedHoldModalProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('pick');
  const [selectedBasketId, setSelectedBasketId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<TreasuryResult | null>(null);
  const [appliedResult, setAppliedResult] = useState<TreasuryResult | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  const holdsQuery = useQuery({
    queryKey: ['treasury', 'stranded-holds', walletId],
    queryFn: () => treasuryService.getStrandedHolds({ walletId, limit: 200 }),
    enabled: open && !!walletId,
  });

  const items: StrandedHold[] = holdsQuery.data?.items ?? [];
  const selected = useMemo(
    () => items.find((h) => h.basketId === selectedBasketId) ?? null,
    [items, selectedBasketId],
  );

  // Reset when the modal is reopened — a stale selection would prefill a stale
  // ceiling, which is the one thing this screen exists to prevent.
  useEffect(() => {
    if (!open) return;
    setStep('pick');
    setSelectedBasketId(null);
    setAmount('');
    setReason('');
    setError(null);
    setPreviewResult(null);
    setAppliedResult(null);
    setIdempotencyKey(newIdempotencyKey());
  }, [open, walletId]);

  // A single stranded hold needs no picking.
  useEffect(() => {
    if (step === 'pick' && items.length === 1 && !selectedBasketId) {
      choose(items[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, items.length]);

  function choose(hold: StrandedHold) {
    setSelectedBasketId(hold.basketId);
    // Prefilled, not typed. `amount` IS the ceiling the backend enforces.
    setAmount(hold.amount.toFixed(2));
    setReason(
      `Clear stranded hold on basket ${shortId(hold.basketId)} (${hold.basketStatus}) — ` +
        `${hold.intended.verdict}. ${hold.intended.reason}`.slice(0, 500),
    );
    setError(null);
    setStep('review');
  }

  const parsedAmount = Number.parseFloat(amount);
  const ceiling = selected?.amount ?? 0;
  const overCeiling = Number.isFinite(parsedAmount) && parsedAmount > ceiling + 1e-9;
  const amountValid =
    Number.isFinite(parsedAmount) && parsedAmount > 0 && !overCeiling;
  const canPreview = !!selected && amountValid && reason.trim().length > 0;

  const buildPayload = () => ({
    walletId,
    amount: parsedAmount,
    reason: reason.trim(),
    direction: 'unhold' as const,
    // Naming the basket is what switches the backend onto the PER-BASKET
    // ceiling instead of the wallet-wide one.
    referenceType: 'order',
    referenceId: selected?.basketId,
    idempotencyKey,
  });

  const previewMutation = useMutation({
    mutationFn: () => treasuryService.preview('adjust-hold', buildPayload()),
    onSuccess: (result) => {
      setPreviewResult(result);
      setError(null);
      setStep('preview');
    },
    onError: (err) => setError(apiMessage(err, 'Could not preview this clear.')),
  });

  const applyMutation = useMutation({
    mutationFn: () => treasuryService.apply('adjust-hold', buildPayload()),
    onSuccess: (result) => {
      setAppliedResult(result);
      setError(null);
      setStep('done');
      toast.success(
        result.idempotentReplay
          ? 'Already cleared — nothing moved a second time.'
          : 'Stranded hold cleared.',
      );
      queryClient.invalidateQueries({ queryKey: ['ledger'] });
      queryClient.invalidateQueries({ queryKey: ['treasury'] });
      onDone?.();
    },
    onError: (err) => setError(apiMessage(err, 'Could not clear this hold.')),
  });

  const busy = previewMutation.isPending || applyMutation.isPending;

  if (!open) return null;

  const total = items.reduce((sum, h) => sum + h.amount, 0);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/55 p-4">
      <div className="my-8 w-full max-w-3xl rounded-3xl bg-[var(--bg-surface)] shadow-lift">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] px-6 py-5">
          <div className="min-w-0">
            <h2 className="font-display text-[20px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Clear a stranded hold
            </h2>
            <p className="mt-1 text-[13px] text-[var(--text-muted)]">
              {walletLabel ?? 'This wallet'} · an earmark no release path will
              ever consume.
              Clearing it moves no money — it only unlocks the earmark.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-5">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-2xl bg-[var(--state-danger-soft,rgba(220,38,38,0.08))] px-4 py-3">
              <ShieldAlert
                className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]"
                strokeWidth={1.5}
              />
              <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
            </div>
          )}

          {/* ── 1. Pick the hold ───────────────────────────────────────── */}
          {step === 'pick' && (
            <>
              {holdsQuery.isLoading && (
                <div className="flex items-center gap-2 py-8 text-[13px] text-[var(--text-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                  Working out where this money came from…
                </div>
              )}

              {!holdsQuery.isLoading && items.length === 0 && (
                <div className="flex items-start gap-2 rounded-2xl bg-[var(--bg-base)] px-4 py-4">
                  <CheckCircle2
                    className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-success)]"
                    strokeWidth={1.5}
                  />
                  <p className="text-[13px] text-[var(--text-secondary)]">
                    Nothing stranded on this wallet. Every hold here is earmarked
                    against a basket still in flight — the release path will clear
                    those on its own, and clearing them by hand would double-pay.
                  </p>
                </div>
              )}

              {items.length > 0 && (
                <>
                  <p className="mb-3 text-[13px] text-[var(--text-secondary)]">
                    <strong className="font-semibold text-[var(--text-primary)]">
                      {items.length}
                    </strong>{' '}
                    stranded {items.length === 1 ? 'hold' : 'holds'} totalling{' '}
                    <strong className="font-semibold text-[var(--text-primary)]">
                      {formatCurrency(total)}
                    </strong>
                    . Work them one basket at a time — each carries its own ceiling.
                  </p>
                  <div className="space-y-2">
                    {items.map((h) => (
                      <button
                        key={h.basketId}
                        type="button"
                        onClick={() => choose(h)}
                        className="flex w-full items-center justify-between gap-4 rounded-2xl bg-[var(--bg-base)] px-4 py-3 text-left transition-colors hover:bg-[var(--bg-elevated,var(--bg-base))]"
                      >
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-[var(--text-primary)]">
                            {formatCurrency(h.amount)}
                            <span className="ml-2 text-[12px] font-normal text-[var(--text-muted)]">
                              basket {shortId(h.basketId)} · {h.basketStatus}
                            </span>
                          </div>
                          <p className="mt-0.5 truncate text-[12px] text-[var(--text-muted)]">
                            {h.intended.recommendedAction}
                          </p>
                        </div>
                        <ArrowRight
                          className="h-4 w-4 shrink-0 text-[var(--text-muted)]"
                          strokeWidth={1.5}
                        />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── 2. Review: what happened, where it goes ────────────────── */}
          {step === 'review' && selected && (
            <div className="space-y-5">
              <StoryBlock hold={selected} />

              {/* The verdict drives whether clearing is the whole job. */}
              <VerdictBlock hold={selected} />

              {/* Amount — prefilled and capped. */}
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                  Amount to clear
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={ceiling}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className={cn(
                      'w-44 rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[14px] text-[var(--text-primary)] outline-none',
                      overCeiling && 'ring-2 ring-[var(--state-danger)]',
                    )}
                  />
                  {parsedAmount !== ceiling && (
                    <button
                      type="button"
                      onClick={() => setAmount(ceiling.toFixed(2))}
                      className="text-[12px] font-semibold text-[var(--brand-600,var(--text-primary))] hover:underline"
                    >
                      Reset to {formatCurrency(ceiling)}
                    </button>
                  )}
                </div>
                {overCeiling ? (
                  <p className="mt-2 flex items-start gap-1.5 text-[12px] text-[var(--state-danger)]">
                    <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                    Only {formatCurrency(ceiling)} is held for this basket.
                    Clearing more would eat another basket's earmark — the
                    backend will reject it.
                  </p>
                ) : (
                  <p className="mt-2 text-[12px] text-[var(--text-muted)]">
                    Capped at {formatCurrency(ceiling)} — this basket's own
                    outstanding earmark on this wallet, not the wallet's whole
                    held balance.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                  Reason <span className="text-[var(--text-muted)]">(audited)</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="w-full rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none"
                />
              </div>
            </div>
          )}

          {/* ── 3. Preview ─────────────────────────────────────────────── */}
          {step === 'preview' && selected && previewResult && (
            <ProjectionBlock
              title="Dry run — nothing has been written yet"
              hold={selected}
              result={previewResult}
            />
          )}

          {/* ── 4. Done ────────────────────────────────────────────────── */}
          {step === 'done' && selected && appliedResult && (
            <>
              <div className="mb-4 flex items-start gap-2 rounded-2xl bg-[var(--bg-base)] px-4 py-3">
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-success)]"
                  strokeWidth={1.5}
                />
                <p className="text-[13px] text-[var(--text-secondary)]">
                  Hold cleared. Journal entry{' '}
                  <code className="text-[12px]">
                    {shortId(appliedResult.journalEntryId)}
                  </code>{' '}
                  posted — balances unchanged, earmark released.
                </p>
              </div>
              <ProjectionBlock
                title="Resulting balances"
                hold={selected}
                result={appliedResult}
              />
            </>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 border-t border-[var(--border-subtle)] px-6 py-4">
          <button
            type="button"
            onClick={
              step === 'review' && items.length > 1
                ? () => setStep('pick')
                : step === 'preview'
                  ? () => setStep('review')
                  : onClose
            }
            className="rounded-xl px-4 py-2 text-[13px] font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-base)]"
          >
            {step === 'review' && items.length > 1
              ? 'Back to list'
              : step === 'preview'
                ? 'Back'
                : 'Close'}
          </button>

          {step === 'review' && (
            <button
              type="button"
              disabled={!canPreview || busy}
              onClick={() => previewMutation.mutate()}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--text-primary)] px-4 py-2 text-[13px] font-semibold text-[var(--bg-surface)] transition-opacity disabled:opacity-40"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
              Preview the clear
            </button>
          )}

          {step === 'preview' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => applyMutation.mutate()}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--state-danger)] px-4 py-2 text-[13px] font-semibold text-white transition-opacity disabled:opacity-40"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
              Clear {formatCurrency(parsedAmount || 0)} for real
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── How the money got here ──────────────────────────────────────────────── */

function StoryBlock({ hold }: { hold: StrandedHold }) {
  const { origin, currentState: cur } = hold;
  const payment = origin.paymentTransactions?.[0];

  return (
    <div className="rounded-2xl bg-[var(--bg-base)] p-4">
      <h3 className="mb-3 text-[13px] font-semibold text-[var(--text-primary)]">
        What happened to this money
      </h3>
      <ol className="space-y-2.5">
        <StoryStep
          label="Customer paid"
          value={
            payment?.amount != null
              ? `${formatCurrency(payment.amount)}${
                  payment.externalTransactionId
                    ? ` · ${payment.externalTransactionId}`
                    : ''
                }${payment.method ? ` · ${payment.method}` : ''}`
              : origin.escrowCredited != null
                ? formatCurrency(origin.escrowCredited)
                : '—'
          }
          at={payment?.completedAt ?? origin.firstSeenAt}
        />
        <StoryStep
          label="Earmarked on this wallet"
          value={`${formatCurrency(hold.amount)} held against basket ${shortId(hold.basketId)}`}
          at={hold.firstHeldAt}
        />
        <StoryStep
          label={`Basket ${hold.basketStatus}`}
          value={
            hold.orderStatus
              ? `order ${shortId(hold.orderId)} · ${hold.orderStatus}`
              : `order ${shortId(hold.orderId)}`
          }
          at={hold.cancelledAt ?? hold.confirmedAt}
        />
        {cur.escrowReversed && (
          <StoryStep
            label="Customer refunded"
            value={
              cur.refundedToCustomer != null
                ? `${formatCurrency(cur.refundedToCustomer)} returned to their wallet`
                : 'escrow reversed'
            }
            at={cur.refundedAt}
            tone="success"
          />
        )}
        {cur.released && (
          <StoryStep
            label="Escrow released"
            value="split paid out to the destination wallets"
            at={cur.releasedAt}
            tone="success"
          />
        )}
        <StoryStep
          label="Earmark never cleared"
          value={`${formatCurrency(hold.amount)} still locked here — this is what you are clearing`}
          at={null}
          tone="danger"
        />
      </ol>

      {cur.escrowStillHolding != null && (
        <p className="mt-3 border-t border-[var(--border-subtle)] pt-3 text-[12px] text-[var(--text-muted)]">
          Cash still sitting in escrow for this basket:{' '}
          <strong
            className={cn(
              'font-semibold',
              cur.escrowStillHolding > 0
                ? 'text-[var(--state-warning,var(--text-primary))]'
                : 'text-[var(--text-primary)]',
            )}
          >
            {formatCurrency(cur.escrowStillHolding)}
          </strong>
          {cur.escrowStillHolding <= 0 &&
            ' — nothing left to move, so clearing the earmark is all that remains.'}
        </p>
      )}
    </div>
  );
}

function StoryStep({
  label,
  value,
  at,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  at?: string | null;
  tone?: 'neutral' | 'success' | 'danger';
}) {
  return (
    <li className="flex items-start gap-3">
      <span
        className={cn(
          'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
          tone === 'success'
            ? 'bg-[var(--state-success)]'
            : tone === 'danger'
              ? 'bg-[var(--state-danger)]'
              : 'bg-[var(--text-muted)]',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[var(--text-primary)]">
          <span className="font-medium">{label}</span>
          <span className="text-[var(--text-muted)]"> — {value}</span>
        </div>
        {at && <div className="text-[11px] text-[var(--text-muted)]">{when(at)}</div>}
      </div>
    </li>
  );
}

/* ── Where the money is supposed to go ───────────────────────────────────── */

function VerdictBlock({ hold }: { hold: StrandedHold }) {
  const { intended } = hold;
  const sufficient = clearingIsSufficient(intended.verdict);

  return (
    <div
      className={cn(
        'rounded-2xl p-4',
        sufficient
          ? 'bg-[var(--bg-base)]'
          : 'bg-[var(--state-warning-soft,rgba(217,119,6,0.10))]',
      )}
    >
      <div className="mb-2 flex items-start gap-2">
        {sufficient ? (
          <Info
            className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-muted)]"
            strokeWidth={1.5}
          />
        ) : (
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-warning,#d97706)]"
            strokeWidth={1.5}
          />
        )}
        <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">
          {sufficient
            ? 'Clearing this hold is the whole job'
            : 'Clearing alone is NOT enough — money still has to move'}
        </h3>
      </div>

      <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
        {intended.reason}
      </p>
      <p className="mt-2 text-[13px] font-medium text-[var(--text-primary)]">
        {intended.recommendedAction}
      </p>

      {intended.destinations.length > 0 ? (
        <div className="mt-3 border-t border-[var(--border-subtle)] pt-3">
          <p className="mb-2 text-[12px] font-semibold text-[var(--text-secondary)]">
            Where it is owed
          </p>
          <ul className="space-y-1.5">
            {intended.destinations.map((d, i) => (
              <li key={`${d.walletId ?? d.party}-${i}`} className="text-[13px]">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[var(--text-secondary)]">
                    <Coins
                      className="mr-1.5 inline h-3.5 w-3.5 text-[var(--text-muted)]"
                      strokeWidth={1.5}
                    />
                    {d.walletLabel ?? d.party}
                    {d.walletLabel && (
                      <span className="text-[var(--text-muted)]"> · {d.party}</span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'font-semibold',
                      d.amount != null
                        ? 'text-[var(--text-primary)]'
                        : 'text-[var(--text-muted)]',
                    )}
                  >
                    {/* null ≠ zero: the split could not be resolved. Saying
                        "KES 0.00" here would read as "nobody is owed anything". */}
                    {d.amount != null ? formatCurrency(d.amount) : 'amount unresolved'}
                  </span>
                </div>
                {d.note && (
                  <p className="mt-0.5 pl-5 text-[12px] text-[var(--state-warning,#d97706)]">
                    {d.note}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[12px] text-[var(--text-muted)]">
            Move that money first (refund or release), then come back and clear
            the earmark — otherwise the record that someone was owed disappears
            with the hold.
          </p>
        </div>
      ) : (
        <p className="mt-3 border-t border-[var(--border-subtle)] pt-3 text-[12px] text-[var(--text-muted)]">
          No wallet should be credited. The cash already reached its destination —
          crediting anyone here would create money that does not exist.
        </p>
      )}
    </div>
  );
}

/* ── Projected balances ──────────────────────────────────────────────────── */

function ProjectionBlock({
  title,
  hold,
  result,
}: {
  title: string;
  hold: StrandedHold;
  result: TreasuryResult;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-[var(--bg-base)] p-4">
        <h3 className="mb-1 text-[13px] font-semibold text-[var(--text-primary)]">
          {title}
        </h3>
        <p className="text-[13px] text-[var(--text-secondary)]">
          Clearing {formatCurrency(result.amount)} held against basket{' '}
          {shortId(hold.basketId)}.{' '}
          <strong className="font-medium text-[var(--text-primary)]">
            No balance moves
          </strong>{' '}
          — only the earmark is released.
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl bg-[var(--bg-base)]">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[12px] text-[var(--text-muted)]">
              <th className="px-4 py-2.5 font-medium">Wallet</th>
              <th className="px-4 py-2.5 text-right font-medium">Balance</th>
              <th className="px-4 py-2.5 text-right font-medium">Held</th>
              <th className="px-4 py-2.5 text-right font-medium">Available</th>
            </tr>
          </thead>
          <tbody>
            {result.balances.map((b) => (
              <tr key={b.walletId} className="border-t border-[var(--border-subtle)]">
                <td className="px-4 py-2.5 text-[var(--text-secondary)]">
                  {b.walletType}
                </td>
                <td className="px-4 py-2.5 text-right text-[var(--text-primary)]">
                  {formatCurrency(b.balance)}
                </td>
                <td className="px-4 py-2.5 text-right text-[var(--text-primary)]">
                  {formatCurrency(b.heldBalance)}
                </td>
                <td className="px-4 py-2.5 text-right text-[var(--text-primary)]">
                  {formatCurrency(b.availableBalance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
